import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import {
  FiSave, FiPlus, FiUser, FiMail, FiPhone, FiMapPin,
  FiBriefcase, FiCreditCard, FiTag, FiX, FiCheck
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import styles from './ClientCreate.module.css';

interface ClientForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  taxId: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  paymentTerms: string;
  notes: string;
  tags: string[];
  status: 'active' | 'inactive';
}

export function ClientCreate() {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [newTag, setNewTag] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<ClientForm>({
    defaultValues: {
      status: 'active',
      tags: [],
      address: {
        country: 'USA'
      }
    }
  });

  const watchedTags = watch('tags');

  const addTag = () => {
    if (newTag.trim() && !watchedTags.includes(newTag.trim())) {
      setValue('tags', [...watchedTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setValue('tags', watchedTags.filter(tag => tag !== tagToRemove));
  };

  const onSubmit = async (data: ClientForm) => {
    try {
      setIsSaving(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('New client:', { ...data, id: uuidv4() });
      toast.success('Client created successfully!');
      navigate('/clients');
    } catch (error) {
      toast.error('Failed to create client');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div 
      className={styles.container} 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            onClick={() => navigate('/clients')}
            className={styles.backButton}
          >
            <FiX size={20} />
            Cancel
          </button>
          <h1 className={styles.title}>Add New Client</h1>
          <p className={styles.subtitle}>Create a new client profile with contact and billing information</p>
        </div>
        
        <div className={styles.headerActions}>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isSaving || !isValid}
            className={`${styles.actionButton} ${styles.saveButton} ${isSaving ? styles.loading : ''}`}
          >
            <FiSave size={16} />
            {isSaving ? 'Creating...' : 'Create Client'}
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          {/* Basic Information */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiUser size={20} />
              Basic Information
            </h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Client Name *</label>
                <input
                  {...register('name', { required: 'Client name is required' })}
                  className={`${styles.input} ${errors.name ? styles.error : ''}`}
                  placeholder="Enter client name"
                />
                {errors.name && (
                  <span className={styles.errorMessage}>{errors.name.message}</span>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Company</label>
                <div className={styles.inputWrapper}>
                  <FiBriefcase className={styles.inputIcon} />
                  <input
                    {...register('company')}
                    className={styles.input}
                    placeholder="Enter company name"
                  />
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email Address *</label>
                <div className={styles.inputWrapper}>
                  <FiMail className={styles.inputIcon} />
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className={`${styles.input} ${errors.email ? styles.error : ''}`}
                    placeholder="Enter email address"
                  />
                </div>
                {errors.email && (
                  <span className={styles.errorMessage}>{errors.email.message}</span>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Phone Number</label>
                <div className={styles.inputWrapper}>
                  <FiPhone className={styles.inputIcon} />
                  <input
                    {...register('phone')}
                    className={styles.input}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tax ID / VAT Number</label>
                <div className={styles.inputWrapper}>
                  <FiCreditCard className={styles.inputIcon} />
                  <input
                    {...register('taxId')}
                    className={styles.input}
                    placeholder="Enter tax ID or VAT number"
                  />
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Status</label>
                <select
                  {...register('status')}
                  className={styles.select}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiMapPin size={20} />
              Address Information
            </h2>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>Street Address</label>
              <input
                {...register('address.street')}
                className={styles.input}
                placeholder="Enter street address"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>City</label>
                <input
                  {...register('address.city')}
                  className={styles.input}
                  placeholder="Enter city"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>State/Province</label>
                <input
                  {...register('address.state')}
                  className={styles.input}
                  placeholder="Enter state or province"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>ZIP/Postal Code</label>
                <input
                  {...register('address.zipCode')}
                  className={styles.input}
                  placeholder="Enter ZIP or postal code"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Country</label>
                <select
                  {...register('address.country')}
                  className={styles.select}
                >
                  <option value="USA">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="UK">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Billing & Preferences */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiCreditCard size={20} />
              Billing & Preferences
            </h2>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>Payment Terms</label>
              <select
                {...register('paymentTerms')}
                className={styles.select}
              >
                <option value="">Select payment terms</option>
                <option value="net15">Net 15</option>
                <option value="net30">Net 30</option>
                <option value="net45">Net 45</option>
                <option value="net60">Net 60</option>
                <option value="due_on_receipt">Due on Receipt</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Notes</label>
              <textarea
                {...register('notes')}
                className={styles.textarea}
                placeholder="Add any additional notes about this client"
                rows={4}
              />
            </div>
          </div>

          {/* Tags */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiTag size={20} />
              Tags
            </h2>
            
            <div className={styles.tagsSection}>
              <div className={styles.addTagForm}>
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  className={styles.tagInput}
                  placeholder="Add a tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <button
                  type="button"
                  onClick={addTag}
                  className={styles.addTagButton}
                  disabled={!newTag.trim()}
                >
                  <FiPlus size={16} />
                </button>
              </div>

              {watchedTags.length > 0 && (
                <div className={styles.tagsList}>
                  {watchedTags.map((tag, index) => (
                    <div key={index} className={styles.tag}>
                      <span className={styles.tagText}>{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className={styles.removeTagButton}
                      >
                        <FiX size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
